import React, { useState } from 'react'
import { gql, useQuery, useMutation } from 'urql'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import CircularProgress from '@mui/material/CircularProgress'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import Spinner from '../loading/components/Spinner'
import { GenericError } from '../error-pages'

const statusQuery = gql`
  query {
    ssncAlertStatus {
      configured
      apiURL
      apiKey
      lastSyncTime
      mappings {
        goalertSchedule
        ssncGroupID
        ssncGroupName
        lastSyncedEmail
        lastSyncTime
        error
      }
    }
  }
`

const testMutation = gql`
  mutation {
    testSSNCAlertSync {
      success
      mappings {
        goalertSchedule
        ssncGroupName
        email
        success
        error
      }
    }
  }
`

const setAPIKeyMutation = gql`
  mutation setSSNCAlertAPIKey($key: String!) {
    setSSNCAlertAPIKey(key: $key)
  }
`

export default function SSNCAlertStatus(): React.JSX.Element {
  const [{ data, fetching, error }, reexecute] = useQuery({
    query: statusQuery,
  })
  const [testResult, runTest] = useMutation(testMutation)
  const [, setAPIKey] = useMutation(setAPIKeyMutation)
  const [showTestResult, setShowTestResult] = useState(false)
  const [newAPIKey, setNewAPIKey] = useState('')
  const [apiKeySaved, setApiKeySaved] = useState(false)

  if (fetching && !data) return <Spinner />
  if (error) return <GenericError error={error.message} />

  const status = data?.ssncAlertStatus
  if (!status?.configured) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity='warning'>
          SS&C Alerting integration is not configured. Set the
          GOALERT_SSNC_ALERT_CONFIG environment variable to enable.
        </Alert>
      </Box>
    )
  }

  const handleTest = (): void => {
    setShowTestResult(true)
    runTest({}).then(() => {
      reexecute({ requestPolicy: 'network-only' })
    })
  }

  const handleSaveAPIKey = (): void => {
    if (!newAPIKey.trim()) return
    setAPIKey({ key: newAPIKey }).then((result) => {
      if (!result.error) {
        setNewAPIKey('')
        setApiKeySaved(true)
        reexecute({ requestPolicy: 'network-only' })
        setTimeout(() => setApiKeySaved(false), 3000)
      }
    })
  }

  const formatTime = (t: string | null): string => {
    if (!t) return 'Never'
    return new Date(t).toLocaleString()
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant='subtitle2' sx={{ mb: 1 }}>
        Configuration
      </Typography>

      <Typography variant='body2' sx={{ mb: 1 }}>
        <strong>API URL:</strong> {status.apiURL}
      </Typography>
      <Typography variant='body2' sx={{ mb: 1 }}>
        <strong>API Key:</strong> {status.apiKey}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
        <TextField
          size='small'
          type='password'
          label='New API Key'
          value={newAPIKey}
          onChange={(e) => setNewAPIKey((e.target as unknown as {value: string}).value)}
          sx={{ width: 300 }}
        />
        <Button
          variant='outlined'
          size='small'
          onClick={handleSaveAPIKey}
          disabled={!newAPIKey.trim()}
        >
          Update Key
        </Button>
        {apiKeySaved && (
          <Alert severity='success' sx={{ py: 0 }}>
            Saved
          </Alert>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant='subtitle2' sx={{ mb: 1 }}>
        Sync Status
      </Typography>
      <Typography variant='body2' sx={{ mb: 2 }}>
        <strong>Last Sync:</strong> {formatTime(status.lastSyncTime)}
      </Typography>

      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>GoAlert Schedule</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>SS&C Group</TableCell>
              <TableCell>Last Sync</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status.mappings.map(
              (m: {
                goalertSchedule: string
                ssncGroupID: number
                ssncGroupName: string
                lastSyncedEmail: string | null
                lastSyncTime: string | null
                error: string | null
              }) => (
                <TableRow key={m.goalertSchedule}>
                  <TableCell>{m.goalertSchedule}</TableCell>
                  <TableCell>{m.lastSyncedEmail || '—'}</TableCell>
                  <TableCell>{m.ssncGroupName}</TableCell>
                  <TableCell>{formatTime(m.lastSyncTime)}</TableCell>
                  <TableCell>
                    {m.error ? (
                      <ErrorIcon color='error' titleAccess={m.error} />
                    ) : m.lastSyncedEmail ? (
                      <CheckCircleIcon color='success' />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />

      <Button
        variant='contained'
        onClick={handleTest}
        disabled={testResult.fetching}
        startIcon={
          testResult.fetching ? <CircularProgress size={16} /> : undefined
        }
      >
        Test Integration
      </Button>

      {showTestResult && testResult.data && (
        <Box sx={{ mt: 2 }}>
          <Alert
            severity={
              testResult.data.testSSNCAlertSync.success ? 'success' : 'error'
            }
          >
            {testResult.data.testSSNCAlertSync.success
              ? 'All mappings synced successfully.'
              : 'One or more mappings failed.'}
          </Alert>
          <Table size='small' sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Schedule</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testResult.data.testSSNCAlertSync.mappings.map(
                (r: {
                  goalertSchedule: string
                  ssncGroupName: string
                  email: string | null
                  success: boolean
                  error: string | null
                }) => (
                  <TableRow key={r.goalertSchedule}>
                    <TableCell>{r.goalertSchedule}</TableCell>
                    <TableCell>{r.email || '—'}</TableCell>
                    <TableCell>{r.ssncGroupName}</TableCell>
                    <TableCell>
                      {r.success ? (
                        <CheckCircleIcon color='success' />
                      ) : (
                        <Typography variant='body2' color='error'>
                          {r.error}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  )
}
